import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useI18n } from '../../i18n'
import { useAuth } from '../AuthProvider'
import {
  fetchLaunchOptions,
  startGatewayScan,
  type ScannerProviderRow,
} from '../../services/scannerGatewayService'
import { Play, X, Info } from 'lucide-react'
import { Select } from '../ui/Select'
import { componentScope } from '../../lib/cssScope'
import { Button } from '@/components/ui/Button'
import shellStyles from '../tpsm/SupplierFormModal.module.css'
import openvasModalStyles from '../../styles/components/assets/OpenVASScanModal.module.css'
import { resolveAssetScanHost } from '../../utils/gvmHosts'
import { resolveWebScanUrl } from '../../utils/scannerWebTarget'

interface Asset {
  id: string
  assetId: string
  name: string
  ipAddress: string
  type?: string
  environment?: string
  scanHost?: string
  details?: Record<string, unknown>
}

interface OpenVASScanCardProps {
  assets: Asset[]
  onScanStarted?: () => void
}

type ScannerInfoKey = 'greenbone' | 'nuclei' | 'zap'

const SCANNER_INFO_KEYS: ScannerInfoKey[] = ['greenbone', 'nuclei', 'zap']

const SCANNER_INFO_FALLBACKS: Record<
  ScannerInfoKey,
  { title: string; description: string; purpose: string; capabilities?: string; important: string; buttonLabel: string }
> = {
  greenbone: {
    buttonLabel: 'Greenbone',
    title: 'Greenbone',
    description:
      'Greenbone (Greenbone Vulnerability Scanner) is a comprehensive vulnerability assessment solution for network infrastructure. Greenbone helps identify security weaknesses in networks, servers, devices, and applications.',
    purpose:
      "Purpose: identify known vulnerabilities, misconfigurations, insecure services, outdated software, and potential security threats across the organization's infrastructure.",
    important:
      'Important: active scanning may affect application performance or stability. It is recommended to run scans during periods of minimal production workload.',
  },
  nuclei: {
    buttonLabel: 'Nuclei',
    title: 'Nuclei',
    description:
      'Nuclei is a fast open-source vulnerability scanner developed by ProjectDiscovery. It uses YAML-based templates to detect vulnerabilities across a wide range of systems and technologies.',
    purpose:
      'Purpose: automated detection of vulnerabilities in web applications, infrastructure, cloud platforms, network devices, and APIs.',
    important:
      'Important: active scanning may affect application performance or stability. It is recommended to run scans during periods of minimal production workload.',
  },
  zap: {
    buttonLabel: 'OWASP ZAP',
    title: 'OWASP ZAP',
    description:
      'OWASP ZAP (Zed Attack Proxy) is a web application security scanner based on the DAST (Dynamic Application Security Testing) methodology. It is developed and maintained by the OWASP (Open Web Application Security Project) community.',
    purpose:
      'Purpose: identify security issues in running web applications and websites by scanning for known vulnerabilities and testing APIs, including REST APIs and GraphQL.',
    capabilities:
      'OWASP ZAP can detect vulnerabilities such as SQL Injection, Cross-Site Scripting (XSS), Clickjacking, insecure HTTP headers, security misconfigurations, and other common web application security issues.',
    important:
      'Important: active scanning may affect application performance or stability. It is recommended to run scans during periods of minimal production workload.',
  },
}

const OpenVASScanCard: React.FC<OpenVASScanCardProps> = ({ assets, onScanStarted }) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [modalOpen, setModalOpen] = useState(false)
  const [infoModal, setInfoModal] = useState<ScannerInfoKey | null>(null)
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [launchProviders, setLaunchProviders] = useState<ScannerProviderRow[]>([])
  const [providerConfigId, setProviderConfigId] = useState('')
  const [scanType, setScanType] = useState('infrastructure')
  const [confirmScan, setConfirmScan] = useState(false)

  const selectedProvider = launchProviders.find((p) => p.id === providerConfigId)
  const isZapProvider = selectedProvider?.providerType === 'OWASP_ZAP'

  useEffect(() => {
    if (!infoModal) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setInfoModal(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [infoModal])

  useEffect(() => {
    if (!modalOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) setModalOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [modalOpen, loading])

  useEffect(() => {
    if (!modalOpen || !token) return
    void fetchLaunchOptions(token)
      .then((rows) => {
        setLaunchProviders(rows)
        const firstSelectable = rows.find((r) => r.selectable)
        if (firstSelectable) {
          setProviderConfigId(firstSelectable.id)
          const st = firstSelectable.scanTypes?.find((s) => !s.disabled)
          if (st) setScanType(st.id)
        }
      })
      .catch(() => setLaunchProviders([]))
  }, [modalOpen, token])

  const handleSelectAll = () => {
    if (selectedAssets.size === assets.length) {
      setSelectedAssets(new Set())
    } else {
      setSelectedAssets(new Set(assets.map((a) => a.id || a.assetId)))
    }
  }

  const handleToggleAsset = (assetId: string) => {
    const newSelected = new Set(selectedAssets)
    if (newSelected.has(assetId)) {
      newSelected.delete(assetId)
    } else {
      newSelected.add(assetId)
    }
    setSelectedAssets(newSelected)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return
    if (!loading) setModalOpen(false)
  }

  const handleStartScan = async () => {
    if (selectedAssets.size === 0) {
      setError(t('assets.scans.selectAtLeastOne', 'Please select at least one asset'))
      return
    }
    if (!providerConfigId) {
      setError(t('assets.scans.selectScanner', 'Select a scanner provider'))
      return
    }
    if (!confirmScan) {
      setError(t('assets.scans.confirmRequired', 'Confirm that you want to run an active vulnerability scan'))
      return
    }
    if (!token) {
      setError(t('assets.scans.notAuthenticated', 'Not authenticated'))
      return
    }

    setLoading(true)
    setError(null)

    try {
      const selectedAssetObjects = assets.filter((a) => selectedAssets.has(a.id || a.assetId))

      const provider = launchProviders.find((p) => p.id === providerConfigId)
      const isZap = provider?.providerType === 'OWASP_ZAP'

      for (const asset of selectedAssetObjects) {
        const assetId = asset.id || asset.assetId
        const target = isZap
          ? resolveWebScanUrl(asset.details, resolveAssetScanHost(asset, asset.details) || asset.ipAddress)
          : resolveAssetScanHost(asset, asset.details) || asset.ipAddress
        if (!target) {
          throw new Error(
            isZap
              ? t(
                  'assets.scans.someAssetsMissingUrl',
                  'Some selected assets have no valid http(s) URL. Add details.url or a hostname before ZAP scanning.',
                )
              : t(
                  'assets.scans.someAssetsMissingIp',
                  'Some selected assets have no valid IP/hostname. Add IPv4 addresses before scanning.',
                ),
          )
        }
        await startGatewayScan(token, {
          providerConfigId,
          assetId,
          target,
          scanType,
          confirmScan: true,
        })
      }

      setModalOpen(false)
      setSelectedAssets(new Set())
      setConfirmScan(false)
      if (onScanStarted) {
        onScanStarted()
      }
      alert(t('assets.scans.scanStarted', 'Scan started successfully'))
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('assets.scans.scanError', 'An error occurred while starting the scan')
      setError(message)
      console.error('Failed to start scan:', err)
    } finally {
      setLoading(false)
    }
  }

  const modal =
    modalOpen && typeof document !== 'undefined' && document.body
      ? createPortal(
          <div
            className={`${componentScope('OpenVASScanModal')} ${shellStyles.backdrop} ${openvasModalStyles.portalBackdrop}`}
            role="presentation"
            onClick={handleBackdropClick}
          >
            <div
              className={`${shellStyles.dialog} ${openvasModalStyles.dialogWide}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="openvas-scan-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={shellStyles.header}>
                <h2 id="openvas-scan-modal-title" className={shellStyles.title}>
                  {t('assets.scans.selectAssets', 'Select Assets for Scanning')}
                </h2>
                <Button
                  type="button"
                  color="outline"
                  iconOnly
                  onClick={() => setModalOpen(false)}
                  disabled={loading}
                  className={shellStyles.closeBtn}
                  aria-label={t('common.close', 'Close')}
                  icon={<X className="h-5 w-5" aria-hidden />}
                >
                  <span className="sr-only">{t('common.close', 'Close')}</span>
                </Button>
              </div>

              <div className={openvasModalStyles.modalBody}>
                <div className={openvasModalStyles.scroll}>
                  {error && (
                    <div className={shellStyles.error} role="alert">
                      {error}
                    </div>
                  )}

                  <div className={openvasModalStyles.toolbar} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                    <label className={openvasModalStyles.selectedHint}>
                      {t('assets.scans.scannerProvider', 'Scanner provider')}
                    </label>
                    <Select
                      value={providerConfigId || null}
                      onChange={(id) => {
                        if (!id) return
                        setProviderConfigId(String(id))
                        const p = launchProviders.find((r) => r.id === id)
                        const st = p?.scanTypes?.find((s) => !s.disabled)
                        if (st) setScanType(st.id)
                      }}
                      placeholder={t('assets.scans.chooseScanner', 'Choose scanner…')}
                      options={launchProviders.map((p) => ({
                        value: p.id,
                        label: p.selectable
                          ? p.displayName
                          : `${p.displayName} — ${p.disabledReason || p.lifecycle?.disabledReason || p.status}`,
                        disabled: !p.selectable,
                      }))}
                    />
                    {launchProviders.length > 0 && !launchProviders.some((p) => p.selectable) && (
                      <div className={openvasModalStyles.selectedHint} role="status" data-testid="scanner-no-launchable">
                        {t(
                          'assets.scans.noLaunchableProviders',
                          'No scanners are launchable yet. Configure and enable a provider in Integrations, then run a health check until status is AVAILABLE.',
                        )}
                      </div>
                    )}
                    {(() => {
                      const selected = launchProviders.find((p) => p.id === providerConfigId)
                      const hint =
                        selected?.lifecycle?.nextAction ||
                        selected?.disabledReason ||
                        selected?.lifecycle?.disabledReason ||
                        null
                      if (!hint || selected?.selectable) return null
                      return (
                        <div className={openvasModalStyles.selectedHint} data-testid="scanner-provider-next-action">
                          {hint}
                        </div>
                      )
                    })()}
                    <label className={openvasModalStyles.selectedHint}>
                      {t('assets.scans.scanType', 'Scan type')}
                    </label>
                    <Select
                      value={scanType}
                      onChange={(id) => id && setScanType(String(id))}
                      options={
                        launchProviders
                          .find((p) => p.id === providerConfigId)
                          ?.scanTypes?.map((st) => ({
                            value: st.id,
                            label: st.label,
                            disabled: st.disabled,
                          })) || [{ value: 'infrastructure', label: 'Infrastructure scan' }]
                      }
                    />
                    <label className={openvasModalStyles.assetRow}>
                      <input
                        type="checkbox"
                        checked={confirmScan}
                        onChange={(e) => setConfirmScan(e.target.checked)}
                      />
                      <span>
                        {t(
                          'assets.scans.confirmScanLabel',
                          'I confirm this active scan is authorized for the selected asset targets.',
                        )}
                      </span>
                    </label>
                  </div>

                  <div className={openvasModalStyles.toolbar}>
                    <Button
                      type="button"
                      color="outline"
                      size="small"
                      onClick={handleSelectAll}
                      className={openvasModalStyles.linkish}
                    >
                      {selectedAssets.size === assets.length
                        ? t('assets.scans.deselectAll', 'Deselect All')
                        : t('assets.scans.selectAll', 'Select All')}
                    </Button>
                    <span className={openvasModalStyles.selectedHint}>
                      ({selectedAssets.size} {t('assets.scans.selected', 'selected')})
                    </span>
                  </div>

                  <div className={openvasModalStyles.assetList}>
                    {assets.length === 0 ? (
                      <div className={openvasModalStyles.empty}>{t('assets.scans.noAssets', 'No assets available')}</div>
                    ) : (
                      assets.map((asset) => {
                        const assetId = asset.id || asset.assetId
                        const isSelected = selectedAssets.has(assetId)
                        const scanTarget =
                          asset.scanHost ||
                          resolveAssetScanHost(asset, asset.details) ||
                          asset.ipAddress ||
                          ''
                        return (
                          <label
                            key={assetId}
                            className={`${openvasModalStyles.assetRow} ${isSelected ? openvasModalStyles.assetRowSelected : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleAsset(assetId)}
                              className={openvasModalStyles.checkbox}
                            />
                            <div className={openvasModalStyles.assetMain}>
                              <div className={openvasModalStyles.assetName}>
                                {asset.name || asset.ipAddress || 'Unknown Asset'}
                              </div>
                              <div className={openvasModalStyles.assetMeta}>
                                {scanTarget ? (
                                  <>
                                    {t('assets.scans.scanTarget', 'Scan target')}: {scanTarget}
                                    {asset.type ? ' • ' : ''}
                                  </>
                                ) : (
                                  <span className={openvasModalStyles.missingHost}>
                                    {isZapProvider
                                      ? t(
                                          'assets.scans.noWebTarget',
                                          'No http(s) URL — add details.url or hostname on the asset',
                                        )
                                      : t(
                                          'assets.scans.noScanTarget',
                                          'No IP/hostname — edit asset and add IPv4 or FQDN',
                                        )}
                                  </span>
                                )}
                                {asset.type && `${t('assets.inventory.type', 'Type')}: ${asset.type}`}
                                {asset.environment &&
                                  ` • ${t('assets.inventory.environment', 'Environment')}: ${asset.environment}`}
                              </div>
                            </div>
                          </label>
                        )
                      })
                    )}
                  </div>
                </div>

                <div className={`${shellStyles.footer} ${openvasModalStyles.footerPad}`}>
                  <Button type="button" color="outline" onClick={() => setModalOpen(false)} disabled={loading}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button
                    type="button"
                    color="primary"
                    onClick={() => void handleStartScan()}
                    disabled={loading || selectedAssets.size === 0}
                    icon={loading ? undefined : <Play className="h-4 w-4" aria-hidden />}
                    iconPosition="end"
                  >
                    {loading ? t('assets.scans.starting', 'Starting...') : t('assets.scans.startScan', 'Start Scan')}
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null

  const infoModalPortal =
    infoModal && typeof document !== 'undefined' && document.body
      ? createPortal(
          (() => {
            const fb = SCANNER_INFO_FALLBACKS[infoModal]
            const infoKey = (field: 'title' | 'description' | 'purpose' | 'capabilities' | 'important') =>
              t(`assets.scans.scannerInfo.${infoModal}.${field}`, fb[field] || '')
            return (
          <div
            className={`${componentScope('ScannerInfoModal')} ${shellStyles.backdrop} ${openvasModalStyles.portalBackdrop}`}
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) setInfoModal(null)
            }}
          >
            <div
              className={`${shellStyles.dialog} ${openvasModalStyles.dialogWide} ${openvasModalStyles.infoDialog}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="scanner-info-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={shellStyles.header}>
                <h2 id="scanner-info-modal-title" className={shellStyles.title}>
                  {infoKey('title')}
                </h2>
                <Button
                  type="button"
                  color="outline"
                  iconOnly
                  onClick={() => setInfoModal(null)}
                  className={shellStyles.closeBtn}
                  aria-label={t('common.close', 'Close')}
                  icon={<X className="h-5 w-5" aria-hidden />}
                >
                  <span className="sr-only">{t('common.close', 'Close')}</span>
                </Button>
              </div>
              <div className={openvasModalStyles.infoBody}>
                <p>{infoKey('description')}</p>
                <p>{infoKey('purpose')}</p>
                {infoModal === 'zap' && fb.capabilities && (
                  <p>{infoKey('capabilities')}</p>
                )}
                <p className={openvasModalStyles.infoImportant}>{infoKey('important')}</p>
              </div>
              <div className={openvasModalStyles.infoFooter}>
                <Button type="button" color="outline" onClick={() => setInfoModal(null)}>
                  {t('common.close', 'Close')}
                </Button>
              </div>
            </div>
          </div>
            )
          })(),
          document.body,
        )
      : null

  return (
    <>
      <div
        className={`${componentScope('OpenVASScanCard')} ds-card flex h-full min-h-0 w-full min-w-0 flex-col rounded-[1.25rem] border border-[var(--ds-border,#e5e7eb)] p-4 shadow-[0_2px_10px_rgba(15,23,42,0.07)]`}
      >
        <div className="space-y-1 mb-3">
          <h3 className="text-base font-semibold text-[var(--ds-text,#111827)]">
            {t('assets.scans.scannerGateway', 'Scanner Gateway')}
          </h3>
          <p className="text-xs text-gray-500">
            {t('assets.scans.scannerGatewayDescription', 'Run scans via enabled scanner providers')}
          </p>
        </div>
        <div className={openvasModalStyles.infoButtonsRow}>
          {SCANNER_INFO_KEYS.map((key) => (
            <Button
              key={key}
              type="button"
              color="outline"
              size="small"
              className={openvasModalStyles.infoButton}
              onClick={() => setInfoModal(key)}
              icon={<Info className="h-3.5 w-3.5" aria-hidden />}
              iconPosition="start"
            >
              {t(
                `assets.scans.scannerInfo.${key}.buttonLabel`,
                SCANNER_INFO_FALLBACKS[key].buttonLabel,
              )}
            </Button>
          ))}
        </div>
        <Button
          type="button"
          color="primary"
          fullWidth
          className="mt-3"
          onClick={() => setModalOpen(true)}
          icon={<Play className="h-4 w-4" aria-hidden />}
          iconPosition="end"
        >
          {t('assets.scans.runScan', 'Run Scan')}
        </Button>
      </div>
      {modal}
      {infoModalPortal}
    </>
  )
}

export default OpenVASScanCard
